import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for WebSocket connection with auto-reconnect
 * Provides real-time updates for Dashboard
 */
export const useWebSocket = (user) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [realtimeStats, setRealtimeStats] = useState({
    revenue_today: 0,
    expenses_today: 0,
    profit_today: 0,
    queue_count: 0,
    barbers_count: 0,
    online_users: { total: 0, admins: 0, barbers: 0 }
  });
  const [notifications, setNotifications] = useState([]);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  
  // Use environment variable for backend URL
  const API_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin) || (typeof window !== 'undefined' ? window.location.origin : '');
  const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/realtime';

  const connect = useCallback(() => {
    if (!user?.id || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      console.log('Connecting to WebSocket:', WS_URL);
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        // Send authentication
        ws.send(JSON.stringify({
          type: 'auth',
          user_id: user.id,
          user_name: user.name,
          user_role: user.role
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setLastMessage(message);
          handleMessage(message);
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;
        
        // Auto-reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }, [user, WS_URL]);

  const handleMessage = useCallback((message) => {
    switch (message.type) {
      case 'initial_data':
        setRealtimeStats(message.data);
        break;
        
      case 'online_users':
        setOnlineUsers(message.data || []);
        if (message.count !== undefined) {
          setRealtimeStats(prev => ({
            ...prev,
            online_users: {
              total: message.count,
              admins: message.data?.filter(u => u.role === 'admin').length || 0,
              barbers: message.data?.filter(u => u.role === 'barber').length || 0
            }
          }));
        }
        break;
        
      case 'new_revenue':
        // Update revenue stats
        setRealtimeStats(prev => ({
          ...prev,
          revenue_today: prev.revenue_today + (message.data?.amount || 0),
          profit_today: prev.profit_today + (message.data?.amount || 0)
        }));
        // Add notification
        addNotification({
          type: 'revenue',
          title: 'إيراد جديد',
          message: `${message.data?.barber_name || 'حلاق'}: ${message.data?.amount} د.إ`,
          data: message.data
        });
        break;
        
      case 'new_booking':
        // Update queue count
        setRealtimeStats(prev => ({
          ...prev,
          queue_count: prev.queue_count + 1
        }));
        // Add notification
        addNotification({
          type: 'booking',
          title: 'حجز جديد',
          message: `${message.data?.customer_name} - ${message.data?.booking_time}`,
          data: message.data
        });
        break;
        
      case 'queue_update':
        if (message.data?.queue_count !== undefined) {
          setRealtimeStats(prev => ({
            ...prev,
            queue_count: message.data.queue_count
          }));
        }
        break;
        
      case 'new_expense':
        setRealtimeStats(prev => ({
          ...prev,
          expenses_today: prev.expenses_today + (message.data?.amount || 0),
          profit_today: prev.profit_today - (message.data?.amount || 0)
        }));
        addNotification({
          type: 'expense',
          title: 'مصروف جديد',
          message: `${message.data?.category}: ${message.data?.amount} د.إ`,
          data: message.data
        });
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      default:
        console.log('Unknown message type:', message.type);
    }
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [
      { ...notification, id, timestamp: new Date().toISOString() },
      ...prev.slice(0, 9) // Keep last 10 notifications
    ]);
    
    // Play sound alert
    playNotificationSound(notification.type);
    
    // Vibrate on mobile
    vibrateDevice(notification.type);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 10000);
  }, []);

  // Sound notification
  const playNotificationSound = useCallback((type) => {
    try {
      // Create audio context for notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different sounds for different notification types
      switch (type) {
        case 'booking':
          oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 - higher for booking
          oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1);
          break;
        case 'revenue':
          oscillator.frequency.setValueAtTime(523, audioContext.currentTime); // C5 - pleasant for revenue
          oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
          oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
          break;
        case 'expense':
          oscillator.frequency.setValueAtTime(330, audioContext.currentTime); // E4 - lower for expense
          break;
        default:
          oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4 - default
      }
      
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // Audio not supported or blocked
      console.log('Audio notification not available');
    }
  }, []);

  // Vibration for mobile devices
  const vibrateDevice = useCallback((type) => {
    if ('vibrate' in navigator) {
      switch (type) {
        case 'booking':
          navigator.vibrate([100, 50, 100, 50, 100]); // Triple vibration for booking
          break;
        case 'revenue':
          navigator.vibrate([200, 100, 200]); // Double vibration for revenue
          break;
        case 'expense':
          navigator.vibrate(100); // Single short vibration
          break;
        default:
          navigator.vibrate(150);
      }
    }
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const requestStats = useCallback(() => {
    sendMessage({ type: 'get_stats' });
  }, [sendMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect when user is available
  useEffect(() => {
    if (user?.id) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [user?.id, connect, disconnect]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;
    
    const interval = setInterval(() => {
      sendMessage({ type: 'ping' });
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [isConnected, sendMessage]);

  return {
    isConnected,
    lastMessage,
    onlineUsers,
    realtimeStats,
    notifications,
    sendMessage,
    requestStats,
    dismissNotification,
    connect,
    disconnect
  };
};

export default useWebSocket;
